import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { toast } from "sonner"

import { useAuth } from "@/lib/auth/use-auth"
import { useFeatureFlags } from "@/features/feature-flags/queries/feature-flags-queries"
import {
  type ActiveSiteNotesDraftSummary,
  listActiveSiteNotesDraftsForUser,
} from "@/features/projects/api/projects-api"
import { getActiveDraft } from "../api/assistant-api"
import { useChatPanel } from "../hooks/use-chat-panel"
import type { Issue } from "../types/assistant.types"

const STALE_THRESHOLD_MS = 60 * 60 * 1000 // 1 hour
const SHOWN_KEY_PREFIX = "stale-draft-shown:"

function isStale(updatedAt: string): boolean {
  const updated = new Date(updatedAt).getTime()
  return Number.isFinite(updated) && Date.now() - updated >= STALE_THRESHOLD_MS
}

/** Mark a draft as already nudged so we don't pester the user every
 *  navigation. The session-storage key is keyed by draft kind + id —
 *  picking the conversation back up resets the staleness clock on the
 *  backend (each turn bumps ``updated_at``) so a future stale window
 *  can re-toast. */
function alreadyNudged(key: string): boolean {
  return sessionStorage.getItem(SHOWN_KEY_PREFIX + key) === "1"
}
function markNudged(key: string): void {
  sessionStorage.setItem(SHOWN_KEY_PREFIX + key, "1")
}

/** Mounted in the authenticated layout. Once per session, on first
 *  load, fans out to the issue-reporting + site-notes "active draft"
 *  endpoints and surfaces a sonner toast for any draft idle longer
 *  than ``STALE_THRESHOLD_MS``. The toast links the user back so they
 *  can resume or submit. Silent if the user has no drafts (or all are
 *  fresh). */
export function StaleDraftNudge() {
  const { user, isAdmin, isEmployee } = useAuth()
  // Read the underlying flags query directly (rather than the
  // ``useFeatureFlag`` boolean wrapper) so we can wait for it to
  // settle. ``useFeatureFlag`` returns ``false`` while loading — if
  // we treat that as the final answer, the issue-draft query stays
  // disabled, the effect below sees ``isLoading=false`` for the
  // disabled query, and ``fired.current`` flips true. By the time
  // flags actually resolve true and the issue query starts running,
  // we've already self-disqualified the nudge.
  const flagsQuery = useFeatureFlags()
  const assistantEnabled = Boolean(flagsQuery.data?.flags?.ai_assistant)
  const fired = useRef(false)
  const openIssuePanel = useChatPanel((s) => s.open)
  const navigate = useNavigate()

  // Each query is gated against the same access surface as its endpoint.
  //   - issues drafts: /api/issues/drafts/active/ requires the
  //     ``ai_assistant`` feature flag (see IsAssistantEnabled). Firing
  //     this for users without the flag would 403 + trigger a toast.
  //   - site-notes drafts: /api/projects/site-notes-drafts/active/ is
  //     staff-only. Customers / personal users would 403 here.
  // ``retry: false`` so a one-off 403 doesn't get retried and re-toast.
  // We additionally gate the issue query on the flags query having
  // settled so we don't fire it with a stale ``false`` from the loading
  // state (see comment on ``flagsQuery`` above).
  const flagsSettled = !flagsQuery.isLoading
  const issueDraftEnabled = !!user && flagsSettled && assistantEnabled
  const siteNotesEnabled = !!user && (isAdmin || isEmployee)

  const issueDraftQuery = useQuery({
    queryKey: ["stale-draft", "issue"],
    queryFn: getActiveDraft,
    enabled: issueDraftEnabled,
    staleTime: STALE_THRESHOLD_MS,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const siteNotesQuery = useQuery({
    queryKey: ["stale-draft", "site-notes"],
    queryFn: listActiveSiteNotesDraftsForUser,
    enabled: siteNotesEnabled,
    staleTime: STALE_THRESHOLD_MS,
    refetchOnWindowFocus: false,
    retry: false,
  })

  useEffect(() => {
    if (fired.current) return
    // Wait for the flags fetch first — otherwise ``issueDraftEnabled``
    // is forced ``false``, the disabled query reports
    // ``isLoading=false`` immediately, and we'd lock the nudge out
    // before the issue query ever ran.
    if (flagsQuery.isLoading) return
    if (issueDraftQuery.isLoading || siteNotesQuery.isLoading) return
    // ``isPending && !isFetching`` is React Query's "enabled=false"
    // shape: never fetched, never will. That's a settled state for
    // our purposes — it means the user isn't entitled to that surface.
    fired.current = true

    const issue: Issue | null | undefined = issueDraftQuery.data
    if (
      issue &&
      issue.conversation_transcript.length > 0 &&
      isStale(issue.updated_at) &&
      !alreadyNudged(`issue:${issue.id}`)
    ) {
      markNudged(`issue:${issue.id}`)
      toast("You have an unfinished issue draft", {
        description: "Reopen the assistant to submit or discard it.",
        duration: 10_000,
        action: {
          label: "Resume",
          onClick: () => openIssuePanel(),
        },
      })
    }

    const siteNotes: ActiveSiteNotesDraftSummary[] = siteNotesQuery.data ?? []
    const staleSiteNotes = siteNotes.filter(
      (d) =>
        d.message_count > 0 &&
        isStale(d.updated_at) &&
        !alreadyNudged(`site-notes:${d.id}`),
    )
    // One toast per stale draft — they may be on different projects so
    // a single combined toast wouldn't link cleanly. Cap at 3 to avoid
    // spamming on the rare day someone has a backlog.
    for (const draft of staleSiteNotes.slice(0, 3)) {
      markNudged(`site-notes:${draft.id}`)
      toast("Unfinished site notes draft", {
        description: `On project: ${draft.project.name}. Resume to finalize or discard.`,
        duration: 10_000,
        action: {
          label: "Open project",
          onClick: () => navigate(`/projects/${draft.project.id}`),
        },
      })
    }
  }, [
    flagsQuery.isLoading,
    issueDraftQuery.isLoading,
    issueDraftQuery.data,
    siteNotesQuery.isLoading,
    siteNotesQuery.data,
    openIssuePanel,
    navigate,
  ])

  return null
}
